import React, { ReactNode, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useRouter } from '../router/Router';

export interface RouteGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export const SUPER_ADMIN_EMAILS = [
  'omarkhaled20262@gmail.com',
  'admin@otostudents.com',
  'whdan2026@otostudents.com',
  'wahdan2020@otostudents.com',
  'nashgo2026@otostudents.com',
];

export const ADMIN_EMAILS = SUPER_ADMIN_EMAILS;

export const isSuperAdminUser = (user: { email?: string | null } | null): boolean => {
  if (!user || !user.email) return false;
  const email = user.email.trim().toLowerCase();
  return SUPER_ADMIN_EMAILS.includes(email);
};

export const isAdminUser = (user: { email?: string | null } | null): boolean => {
  if (!user || !user.email) return false;
  const email = user.email.trim().toLowerCase();
  if (SUPER_ADMIN_EMAILS.includes(email)) return true;
  if (email.endsWith('@otostudents.com')) return true;
  return false;
};

/**
 * PublicRoute: Accessible by unauthenticated users (Login, Register, Forgot Password).
 * If the user is already authenticated:
 * - If profile is incomplete -> redirects to /complete-profile
 * - If profile is complete and active -> redirects to /student or /admin
 */
export const PublicRoute: React.FC<RouteGuardProps> = ({ children }) => {
  const { user, authState, isLoading, isSuperAdmin, isOperator } = useAuth();
  const { navigate } = useRouter();

  useEffect(() => {
    if (!isLoading) {
      const hasAdminRights = isSuperAdmin || isOperator || isAdminUser(user);
      if (authState === 'AUTHENTICATED') {
        if (hasAdminRights) {
          navigate('/admin');
        } else {
          navigate('/student');
        }
      } else if (authState === 'PROFILE_INCOMPLETE') {
        if (hasAdminRights) {
          navigate('/admin');
        } else {
          navigate('/complete-profile');
        }
      }
    }
  }, [isLoading, authState, user, isSuperAdmin, isOperator, navigate]);

  if (isLoading || authState === 'AUTHENTICATED' || authState === 'PROFILE_INCOMPLETE') {
    return (
      <div className="auth-loading-screen" dir="rtl">
        <div className="loading-spinner"></div>
        <p>جاري التحقق من الجلسة...</p>
      </div>
    );
  }

  return <>{children}</>;
};

/**
 * ProtectedRoute: Base guard requiring verified Firebase authentication.
 * If user is not authenticated -> redirects to /login.
 */
export const ProtectedRoute: React.FC<RouteGuardProps> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const { navigate } = useRouter();

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    }
  }, [isLoading, user, navigate]);

  if (isLoading || !user) {
    return (
      <div className="auth-loading-screen" dir="rtl">
        <div className="loading-spinner"></div>
        <p>جاري التحقق من الحساب...</p>
      </div>
    );
  }

  return <>{children}</>;
};

/**
 * StudentRoute: Requires authenticated student with status !== 'SUSPENDED'.
 * If student is SUSPENDED, renders a clear suspension notice.
 */
export const StudentRoute: React.FC<RouteGuardProps> = ({ children }) => {
  const { user, studentProfile, authState, isLoading, logout } = useAuth();
  const { navigate } = useRouter();

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    }
  }, [isLoading, user, navigate]);

  if (isLoading) {
    return (
      <div className="auth-loading-screen" dir="rtl">
        <div className="loading-spinner"></div>
        <p>جاري تحميل بيانات الطالب...</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // Handle Suspended Student
  if (authState === 'SUSPENDED' || studentProfile?.status === 'SUSPENDED') {
    return (
      <div className="suspended-screen-container" dir="rtl">
        <div className="suspended-card">
          <div className="suspended-icon">⚠️</div>
          <h2 className="suspended-title">الحساب معلق مؤقتًا</h2>
          <p className="suspended-message">
            عذرًا، حساب الطالب الخاص بك <strong>معلق حاليًا</strong> ولا يمكنك تنفيذ أي عمليات أو حجز رحلات.
          </p>
          <div className="suspended-details">
            <p>
              البريد الإلكتروني: <strong>{user.email}</strong>
            </p>
            <p>
              معرّف الحساب (UID): <code>{user.uid}</code>
            </p>
          </div>
          <p className="suspended-help">
            يرجى مراجعة إدارة منصة <strong>OTO STUDENTS للطلاب</strong> عبر الخط الساخن أو خدمة الدعم لفك التعليق وتحديث الحساب.
          </p>
          <div className="suspended-actions">
            <button className="btn-secondary" onClick={() => logout()}>
              تسجيل الخروج
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

/**
 * ProfileCompletedRoute: Requires student to have completed their profile.
 * If profileCompleted is false -> redirects to /complete-profile.
 */
export const ProfileCompletedRoute: React.FC<RouteGuardProps> = ({ children }) => {
  const { user, studentProfile, authState, isLoading } = useAuth();
  const { navigate } = useRouter();

  useEffect(() => {
    if (!isLoading) {
      if (!user) {
        navigate('/login');
      } else if (authState === 'PROFILE_INCOMPLETE' || !studentProfile?.profileCompleted) {
        navigate('/complete-profile');
      }
    }
  }, [isLoading, user, authState, studentProfile?.profileCompleted, navigate]);

  if (isLoading || !user || authState === 'PROFILE_INCOMPLETE' || !studentProfile?.profileCompleted) {
    return (
      <div className="auth-loading-screen" dir="rtl">
        <div className="loading-spinner"></div>
        <p>جاري التحقق من الملف الشخصي...</p>
      </div>
    );
  }

  return <>{children}</>;
};

/**
 * AdminRoute: Restricts access strictly to administrative personnel.
 * Verifies role claim or server-side admin privilege.
 */
export const AdminRoute: React.FC<RouteGuardProps> = ({ children }) => {
  const { user, isLoading, isSuperAdmin, isOperator } = useAuth();
  const { navigate } = useRouter();

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    }
  }, [isLoading, user, navigate]);

  if (isLoading) {
    return (
      <div className="auth-loading-screen" dir="rtl">
        <div className="loading-spinner"></div>
        <p>جاري التحقق من صلاحيات الإدارة...</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const hasAdminAccess = isSuperAdmin || isOperator || isAdminUser(user);
  if (!hasAdminAccess) {
    return (
      <div className="suspended-screen-container" dir="rtl" style={{ margin: '4rem auto', maxWidth: '520px' }}>
        <div className="suspended-card">
          <div className="suspended-icon" style={{ background: '#FEE2E2', color: '#DC2626' }}>⛔</div>
          <h2 className="suspended-title">منطقة مخصصة للإدارة فقط</h2>
          <p className="suspended-message">
            عذرًا، حسابك الحالي (<strong>{user.email}</strong>) لا يمتلك صلاحيات إدارة النظام.
          </p>
          <div className="suspended-actions" style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
            <button className="btn-primary" onClick={() => navigate('/student')}>
              العودة لبوابة الطلاب
            </button>
            <button className="btn-secondary" onClick={() => navigate('/login')}>
              تسجيل الدخول بحساب المشرف
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};